export type InboxSortOption = `newest` | `unread` | `needs_attention`

export const INBOX_SORT_OPTIONS: InboxSortOption[] = [`newest`, `unread`, `needs_attention`]

/** Narrows a persisted setting, which can be stale or hand-edited, to a sort we still support. */
export function isInboxSortOption(value: unknown): value is InboxSortOption {
  return INBOX_SORT_OPTIONS.includes(value as InboxSortOption)
}

/** The only fields the inbox sort reads. Keeps this testable without a full message. */
export type SortableConversation = {
  threadId?: string | null
  unread: number
  createdDate?: string | null
  needsAttention?: boolean
}

/**
 * The selected thread's unread count, frozen at the moment it was selected.
 *
 * Reading a thread marks it read after a short dwell, which under the unread
 * sort would drop it out of the unread block and slide it down the list while
 * the user is still reading it. Holding the count the sort sees keeps the row
 * where it is until the user moves on.
 */
export type PinnedUnread = { threadId: string; unread: number } | undefined

const newestFirst = (a: SortableConversation, b: SortableConversation) =>
  new Date(b.createdDate ?? 0).getTime() - new Date(a.createdDate ?? 0).getTime()

export function sortConversations<T extends SortableConversation>(
  conversations: T[],
  sortBy: InboxSortOption,
  pinnedUnread?: PinnedUnread,
): T[] {
  if (sortBy === `unread`) {
    const unreadForSort = (conversation: T) =>
      conversation.threadId && conversation.threadId === pinnedUnread?.threadId
        ? pinnedUnread.unread
        : conversation.unread
    return [...conversations].sort((a, b) => {
      const aUnread = unreadForSort(a)
      const bUnread = unreadForSort(b)
      if (aUnread > 0 && bUnread === 0) return -1
      if (aUnread === 0 && bUnread > 0) return 1
      return newestFirst(a, b)
    })
  }
  if (sortBy === `needs_attention`) {
    return [...conversations].sort((a, b) => {
      const aNeedsAttention = a.needsAttention ?? false
      const bNeedsAttention = b.needsAttention ?? false
      if (aNeedsAttention && !bNeedsAttention) return -1
      if (!aNeedsAttention && bNeedsAttention) return 1
      return newestFirst(a, b)
    })
  }
  return conversations
}
